import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  Plus,
  Eye,
  Printer,
  CheckCircle2,
  Package,
  User,
  Calendar,
  AlertCircle,
  TrendingDown,
  TrendingUp,
  FileText,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  server_post_data,
  get_adjustment_list,
} from "../ServiceConnection/serviceconnection";

export default function AdjustmentListScreen() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingAdjustment, setViewingAdjustment] = useState(null);

  const fetchAdjustments = useCallback(async () => {
    setLoading(true);
    setError(null);

    const fd = new FormData();
    fd.append("status", "all");
    fd.append("search", "");

    try {
      const response = await server_post_data(get_adjustment_list, fd);

      if (response.data.error) {
        setError(response.data.message || "Failed to load adjustments");
        setAdjustmentData([]);
      } else {
        const rawList = response.data.message?.list || [];

        const formatted = rawList.map((adj) => {
          const items = (adj.items || []).map((it) => ({
            ingredient_id: it.item_id,
            item_name: it.item_name || "Unknown Item",
            current_stock: Number(it.system_qty) || 0,
            adjustment_qty: Number(it.adjustment_qty) || 0,
            adjustment_type: it.difference_type || "Reduced",
            reason: it.reason || "",
            unit_name: it.unit_name || "—",
            stock_before: it?.stock_before != null 
              ? `${it.stock_before} ${it.unit_name || ''}`.trim() 
              : "—",

            stock_after: it?.stock_after != null 
              ? `${it.stock_after} ${it.unit_name || ''}`.trim() 
              : "—",
          }));

          const total_reduced = items.reduce((sum, i) => {
            const type = (i.adjustment_type || "").toLowerCase().trim();
            const qty = Number(i.adjustment_qty) || 0;
            if (
              type === "reduced" ||
              type === "short" ||
              type.includes("loss")
            ) {
              return sum + qty;
            }
            return sum;
          }, 0);

          const total_gain = items.reduce((sum, i) => {
            const type = (i.adjustment_type || "").toLowerCase().trim();
            const qty = Number(i.adjustment_qty) || 0;
            if (type === "gain" || type === "excess" || type.includes("gain")) {
              return sum + qty;
            }
            return sum;
          }, 0);

          return {
            id: adj.adjustment_id,
            adjustment_number: adj.adjustment_no || `ADJ-${adj.adjustment_id}`,
            user_name: adj.admin_name || "—",
            adjustment_date: adj.adjustment_date || adj.createdAt,
            items_count: adj.total_items || items.length,
            total_reduced,
            total_gain,
            status: "Completed",
            created_at:
              adj.createdAt || adj.adjustment_date || new Date().toISOString(),
            adjustment_notes: "",
            items,
          };
        });

        setAdjustmentData(formatted);
      }
    } catch (err) {
      console.error("Fetch adjustments error:", err);
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdjustments();
  }, [fetchAdjustments]);

  const filteredData = useMemo(() => {
    return adjustmentData.filter((adjustment) => {
      const matchesSearch =
        adjustment.adjustment_number
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        adjustment.user_name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        filterStatus === "all" || adjustment.status === filterStatus;

      return matchesSearch && matchesStatus;
    });
  }, [adjustmentData, searchQuery, filterStatus]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleViewClick = useCallback((adjustment) => {
    console.log(adjustment);
    setViewingAdjustment(adjustment);
    setIsViewDialogOpen(true);
  }, []);

  const handleCreateAdjustment = () => {
    navigate("/adjustment-create");
  };

  const handlePrint = (adjustmentId) => {
    console.log("Print adjustment:", adjustmentId);
  };

  const stats = useMemo(() => {
    const completed = adjustmentData.length;
    const pending = 0;
    const total_reduced = adjustmentData.reduce(
      (sum, a) => sum + (Number(a.total_reduced) || 0),
      0,
    );
    const total_gain = adjustmentData.reduce(
      (sum, a) => sum + (Number(a.total_gain) || 0),
      0,
    );

    return { completed, pending, total_reduced, total_gain };
  }, [adjustmentData]);

  return (
    <MainLayout>
      <Header
        title="Stock Adjustments"
        subtitle="View all stock adjustments and inventory corrections"
      />

      <div className="p-6 space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Completed Adjustments
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-primary">
                    {stats.completed}
                  </p>
                  <span className="text-sm text-green-600">✓</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Pending Adjustments
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-orange-500">
                    {stats.pending}
                  </p>
                  <span className="text-sm text-orange-600">○</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Total Reduced</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-red-600">
                    {stats.total_reduced.toFixed(2)}
                  </p>
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Total Gain</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-green-600">
                    {stats.total_gain.toFixed(2)}
                  </p>
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search + Filter + Create */}
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search adjustment number or user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
            </SelectContent>
          </Select>

          <Button
            onClick={handleCreateAdjustment}
            className="gap-2 whitespace-nowrap"
            size="lg"
          >
            <Plus className="w-4 h-4" />
            Create Adjustment
          </Button>
        </div>

        {/* Table */}
        <Card>
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading adjustments...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-destructive">
              <AlertCircle className="h-12 w-12 mx-auto mb-4" />
              <p>{error}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={fetchAdjustments}
              >
                Try Again
              </Button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No adjustments found</p>
              {(searchQuery || filterStatus !== "all") && (
                <p className="text-sm mt-2">
                  Try adjusting your search or filter
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-muted-foreground">
                      Adjustment Number
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Adjusted By
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Items
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Reduced
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Gain
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Date
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((adjustment) => (
                    <TableRow
                      key={adjustment.id}
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={() => handleViewClick(adjustment)}
                    >
                      <TableCell className="font-medium text-primary">
                        {adjustment.adjustment_number}
                      </TableCell>
                      <TableCell>{adjustment.user_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">
                          {adjustment.items_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold text-red-600 flex items-center justify-center gap-1">
                          <TrendingDown className="w-4 h-4" />
                          {adjustment.total_reduced.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold text-green-600 flex items-center justify-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          {adjustment.total_gain.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          ✓ {adjustment.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(adjustment.created_at)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewClick(adjustment);
                            }}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4 text-muted-foreground hover:text-primary" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrint(adjustment.id);
                            }}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                            title="Print"
                          >
                            <Printer className="w-4 h-4 text-muted-foreground hover:text-primary" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* View Dialog */}
<Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
  <DialogContent className="sm:max-w-[1000px] max-h-[85vh] overflow-y-auto">
    <DialogHeader>
      <DialogTitle>Adjustment Details</DialogTitle>
      <DialogDescription>
        Complete information about this adjustment
      </DialogDescription>
    </DialogHeader>

    {viewingAdjustment && (
      <div className="space-y-6 py-4">
        {/* ---------- Header ---------- */}
        <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
              <Package className="h-8 w-8 text-primary-foreground" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">
                {viewingAdjustment.adjustment_number ||
                  viewingAdjustment.adjustment_no}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Adjustment ID: {viewingAdjustment.id ||
                  viewingAdjustment.adjustment_id}
              </p>
              {(viewingAdjustment.department_name ||
                viewingAdjustment.department) && (
                <p className="text-sm text-muted-foreground">
                  {viewingAdjustment.department_name ||
                    viewingAdjustment.department}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Badge className="bg-green-100 text-green-700 border-green-300 font-medium">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {viewingAdjustment.status || "Completed"}
            </Badge>
          </div>
        </div>

        {/* ---------- Summary cards ---------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                Adjusted By
              </p>
            </div>
            <p className="text-base font-semibold text-foreground ml-10">
              {viewingAdjustment.user_name ||
                viewingAdjustment.admin_name ||
                "—"}
            </p>
          </div>

          <div className="p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-primary" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                Adjustment Date
              </p>
            </div>
            <p className="text-base font-semibold text-foreground ml-10">
              {formatDate(
                viewingAdjustment.created_at || viewingAdjustment.createdAt,
              )}
            </p>
          </div>

          <div className="p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-red-600" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                Items Reduced
              </p>
            </div>
            <p className="text-base font-semibold text-red-600 ml-10">
              {
                (viewingAdjustment.items || []).filter((i) =>
                  String(i.difference_type || i.adjustment_type || "")
                    .toLowerCase()
                    .match(/loss|reduce|short/),
                ).length
              }
            </p>
          </div>

          <div className="p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                Items Gained
              </p>
            </div>
            <p className="text-base font-semibold text-green-600 ml-10">
              {
                (viewingAdjustment.items || []).filter((i) =>
                  String(i.difference_type || i.adjustment_type || "")
                    .toLowerCase()
                    .match(/gain|add|excess/),
                ).length
              }
            </p>
          </div>
        </div>

        {/* ---------- Items ---------- */}
        <div className="space-y-3">
          <h4 className="font-semibold text-foreground flex items-center gap-2">
            <Package className="w-5 h-5" />
            Adjustment Items (
            {viewingAdjustment.items_count ||
              viewingAdjustment.items?.length ||
              0}
            )
          </h4>

          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Item Name
                  </TableHead>
                  <TableHead className="text-center text-muted-foreground">
                    Stock Before
                  </TableHead>
                  <TableHead className="text-center text-muted-foreground">
                    Adj Qty
                  </TableHead>
                  <TableHead className="text-center text-muted-foreground">
                    Stock After
                  </TableHead>
                  <TableHead className="text-center text-muted-foreground">
                    Type
                  </TableHead>
                  <TableHead className="text-muted-foreground">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {viewingAdjustment.items?.length > 0 ? (
                  viewingAdjustment.items.map((item, idx) => {
                    const type = String(
                      item.difference_type || item.adjustment_type || "",
                    );
                    const isLoss = !!type
                      .toLowerCase()
                      .match(/loss|reduce|short/);

                    const unit = item.unit_name || item.item_unit_type || "";
                    const before = item.stock_before;
                    const after = item.stock_after;

                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{idx + 1}</TableCell>

                        <TableCell className="font-medium">
                          {item.item_name}
                        </TableCell>

                        <TableCell className="text-center">
                            <span className="font-semibold">
                              {before}
                            </span>
                        </TableCell>

                        <TableCell className="text-center">
                          <span
                            className={cn(
                              "font-semibold",
                              isLoss ? "text-red-600" : "text-green-600",
                            )}
                          >
                            {isLoss ? "−" : "+"}
                            {item.adjustment_qty} {unit}
                          </span>
                        </TableCell>

                        <TableCell className="text-center">
                            <span className="font-semibold">
                              {after}
                            </span>
                        </TableCell>

                        <TableCell className="text-center">
                          <Badge
                            className={cn(
                              "font-semibold",
                              isLoss
                                ? "bg-red-100 text-red-700 border-red-300"
                                : "bg-green-100 text-green-700 border-green-300",
                            )}
                          >
                            {type || "—"}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-sm text-muted-foreground max-w-xs">
                          <span title={item.reason}>{item.reason || "—"}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-muted-foreground py-8"
                    >
                      No items in this adjustment
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <p className="text-xs text-muted-foreground">
            Stock before and after are recorded at the moment the adjustment was
            saved. Older adjustments may show a dash.
          </p>
        </div>

        {/* ---------- Notes ---------- */}
        {viewingAdjustment.adjustment_notes && (
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Adjustment Notes
            </h4>
            <div className="p-4 border rounded-lg bg-muted/30">
              <p className="text-sm text-foreground">
                {viewingAdjustment.adjustment_notes}
              </p>
            </div>
          </div>
        )}
      </div>
    )}

    <div className="flex justify-end gap-3 pt-4 border-t">
      <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
        Close
      </Button>
    </div>
  </DialogContent>
</Dialog>
    </MainLayout>
  );
}
